import type { InstrumentId } from "@trader/shared/MarketData"
import type { AccountSummary, Runner, RunnerReport } from "@trader/shared/Money"
import { useEffect, useState } from "react"
import { messageFor, tradingApi } from "./tradingApi.ts"
import { useAccountSummary, useFills, useRunners, useSyncChart } from "./tradingData.ts"

/** Re-read the report at most this often while fills arrive (a playing replay fills often). */
const REFRESH_MS = 1_000

export interface PaneRunnerReport {
  readonly account: AccountSummary
  readonly runner: Runner
  /** null until the first load. */
  readonly report: RunnerReport | null
  readonly error: string | null
}

/**
 * The followed account's strategy runner on a chart's symbol, and its report
 * (backtest fills next to paper fills, docs/design/0007), re-read as fills arrive.
 * null when no runner trades the symbol on that account.
 */
export const usePaneRunnerReport = (instrumentId: InstrumentId | null): PaneRunnerReport | null => {
  const account = useAccountSummary()
  const runners = useRunners()
  const fills = useFills().length
  const active = runners.filter((runner) => runner.instrumentId === instrumentId)
  const runner = active.find((r) => r.status._tag !== "Stopped") ?? active.at(-1) ?? null
  const [loaded, setLoaded] = useState<{ readonly key: string; readonly report: RunnerReport | null; readonly error: string | null } | null>(null)
  const key = account !== null && runner !== null ? `${account.id}:${runner.id}` : null

  useEffect(() => {
    if (account === null || runner === null || key === null) return
    let live = true
    const timer = setTimeout(() => {
      tradingApi.runnerReport(account.id, runner.id).then(
        (report) => live && setLoaded({ key, report, error: null }),
        (failure: unknown) => live && setLoaded((current) => ({ key, report: current?.key === key ? current.report : null, error: messageFor(failure).detail }))
      )
    }, loaded?.key === key ? REFRESH_MS : 0)
    return () => {
      live = false
      clearTimeout(timer)
    }
    // Re-read when fills arrive or the runner changes (its decisions move updatedAt).
  }, [key, fills, runner?.updatedAt])

  if (account === null || runner === null) return null
  return { account, runner, report: loaded?.key === key ? loaded.report : null, error: loaded?.key === key ? loaded.error : null }
}

/**
 * While the charts follow a Replay account (docs/design/0007), the runner that
 * paper trades the chart's strategy on its symbol there: the chart's backtest runs
 * with the runner's inputs and properties (its capital and start), so the tester
 * shows the same run the paper account follows. null otherwise.
 */
export const useSyncedRunner = (strategyId: string | undefined, instrumentId: InstrumentId): Runner | null => {
  const account = useAccountSummary()
  const sync = useSyncChart()
  const runners = useRunners()
  if (!sync || strategyId === undefined || account === null || account.status === "Archived" || account.clock._tag !== "Replay") return null
  return runners.find((runner) => runner.instrumentId === instrumentId && runner.strategy === strategyId && runner.status._tag !== "Stopped") ?? null
}
